import React, { useState, useEffect } from 'react';
import {
  HelpCircle,
  ChevronDown,
  MessageCircle,
  Sparkles,
  Cog,
  Lock,
  Search,
  FileText,
  Gauge
} from 'lucide-react';
import { useLocaleContext } from '../context/LocaleContext';
import { getTranslation } from '../utils/translations';
import { generateFAQSchema, injectFAQSchema } from '../utils/faqSchema';

interface FAQProps {
  /** When true, renders H2 instead of H1 (for embedding on pages that already have an H1) */
  asSection?: boolean;
}

const FAQ: React.FC<FAQProps> = ({ asSection = false }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { locale } = useLocaleContext();

  // SEO: Use H2 when embedded as a section on homepage, H1 when standalone FAQ page
  const HeadingTag = asSection ? 'h2' : 'h1';

  // Inject FAQ Schema JSON-LD for SEO
  useEffect(() => {
    // Guard against SSR
    if (typeof window === 'undefined') return;

    try {
      // Generate FAQ schema from current locale's FAQ items
      const faqSchema = generateFAQSchema(faqs[locale]);

      // Inject schema into document head
      const cleanup = injectFAQSchema(faqSchema);

      return cleanup;
    } catch (error) {
      console.error('[FAQ] Failed to generate or inject FAQ schema:', error);
    }
  }, [locale]);

  const faqs = {
    en: [
      {
        question: "What are AI watermarks?",
        answer: "AI watermarks are invisible characters, like zero-width spaces, that some AI tools insert into generated text. They don't affect readability, and their presence or absence has no bearing on whether AI-detection tools flag the text.",
        category: "basics"
      },
      {
        question: "How does the watermark removal tool work?",
        answer: "Our tool scans your text for invisible characters commonly used as AI watermarks, such as zero-width spaces and ASCII control characters. It then highlights these markers and can remove them completely while preserving your original content.",
        category: "technical"
      },
      {
        question: "Is my text stored or logged?",
        answer: "No. All text processing happens directly in your browser. We don't store, log, or transmit your text anywhere. Your privacy is our priority.",
        category: "privacy"
      },
      {
        question: "What types of watermarks can be detected?",
        answer: "We detect various invisible characters including zero-width spaces, zero-width joiners, soft hyphens, word joiners, and ASCII control characters that AI tools might use as watermarks.",
        category: "technical"
      },
      {
        question: "Will removing watermarks affect my text?",
        answer: "No. The tool only removes invisible characters used as watermarks. Your visible text, formatting, and meaning remain exactly the same.",
        category: "basics"
      },
      {
        question: "Is there a character limit?",
        answer: "Yes, the free version currently has a 500-character limit per operation. Signed-in users get 1,500 characters. This helps ensure fast processing and optimal performance.",
        category: "limits"
      },
      {
        question: "Can I clean watermarks from Word documents?",
        answer: "Yes! You can upload Word (.docx) documents directly to our tool. We'll scan and remove invisible watermark characters from all sections including main content, headers, footers, footnotes, and comments—all while preserving your document's formatting and layout. Processing happens entirely in your browser, so your documents remain private.",
        category: "technical"
      },
      {
        question: "Can I clean watermarks from Apple Pages documents?",
        answer: "Yes! You can upload Apple Pages (.pages) documents directly to our tool. We support both modern and legacy Pages formats. We'll scan and remove invisible watermark characters from your entire document while preserving all formatting, styles, and layout. All processing happens locally in your browser, ensuring your documents stay completely private.",
        category: "technical"
      },
      {
        question: "How secure is document processing? Do my documents leave my device?",
        answer: "Your documents are 100% secure and never leave your device. All document processing happens entirely in your browser using JavaScript. When you upload a Word or Pages document, it's read and modified locally on your computer—we never send your files to any server. This means your sensitive documents, personal information, and private content remain completely confidential. Even we can't see what you're processing. This client-side approach ensures maximum privacy and security for your documents.",
        category: "privacy"
      },
      {
        question: "Why does my cleaned document still show watermarks when I copy the text?",
        answer: "If you clean a document using our document uploader and then copy-paste the text into the text detection area, you might see watermarks detected. This is normal and doesn't mean the cleaning failed. Here's why: Document cleaning preserves all formatting (bold, italics, spacing, etc.) to keep your document looking identical. When you copy this formatted text, some harmless formatting characters (like special spaces or unicode formatting marks) come along. Our text detection tool, which is designed to be more aggressive, might flag these formatting characters as potential watermarks. The document itself is clean—these are just formatting artifacts from the copy-paste process, not actual AI watermarks. For best results, use the cleaned document directly rather than copy-pasting its content.",
        category: "technical"
      },
      {
        question: "What is a zero-width character and why do AI tools use them?",
        answer: "Zero-width characters are invisible Unicode characters that take up no visual space but exist in the text data. Some AI systems, including ChatGPT, insert these into generated text as a byproduct of how the content was produced. Common types include zero-width spaces (U+200B), zero-width joiners (U+200D), and zero-width non-joiners (U+200C). Our tool detects and removes these invisible markers.",
        category: "technical"
      },
      {
        question: "How does this compare to other free watermark removers?",
        answer: "Our tool is free, processes text locally in your browser for privacy, and works on text from any source, because it operates on characters rather than on the model. Unlike other tools, we never store your text, offer document cleaning for Word and Pages files, and give signed-in users a 1,500-character limit, unlimited with Premium.",
        category: "basics"
      },
      {
        question: "Can I clean paste text directly from ChatGPT?",
        answer: "Yes! Simply copy text from ChatGPT, Claude, Gemini, or any AI chat and paste it directly into our tool. Our invisible character detector highlights any hidden characters, and you can clean the text with one click. This clean paste workflow is designed to be fast and seamless.",
        category: "basics"
      },
      {
        question: "How do I detect AI watermarks in my text?",
        answer: "Paste your text into our detector tool. We'll scan for dozens of invisible Unicode characters, including zero-width spaces, zero-width joiners and other zero-width and control characters. Detected watermarks are highlighted so you can see exactly where they are in your text.",
        category: "technical"
      }
    ],
    de: [
      {
        question: "Was sind KI-Wasserzeichen?",
        answer: "KI-Wasserzeichen sind unsichtbare Zeichen, wie Leerzeichen ohne Breite, die manche KI-Tools in generierten Text einfügen. Sie beeinträchtigen nicht die Lesbarkeit, und ihr Vorhandensein oder Fehlen hat keinen Einfluss darauf, ob KI-Erkennungstools den Text als KI-generiert einstufen.",
        category: "basics"
      },
      {
        question: "Wie funktioniert das Wasserzeichen-Entfernungstool?",
        answer: "Unser Tool scannt Ihren Text nach unsichtbaren Zeichen, die häufig als KI-Wasserzeichen verwendet werden, wie zum Beispiel Leerzeichen ohne Breite und ASCII-Steuerzeichen. Es hebt diese Markierungen dann hervor und kann sie vollständig entfernen, während Ihr ursprünglicher Inhalt erhalten bleibt.",
        category: "technical"
      },
      {
        question: "Wird mein Text gespeichert oder protokolliert?",
        answer: "Nein. Die gesamte Textverarbeitung erfolgt direkt in Ihrem Browser. Wir speichern, protokollieren oder übertragen Ihren Text nirgendwohin. Ihre Privatsphäre hat für uns Priorität.",
        category: "privacy"
      },
      {
        question: "Welche Arten von Wasserzeichen können erkannt werden?",
        answer: "Wir erkennen verschiedene unsichtbare Zeichen, einschließlich Leerzeichen ohne Breite, Verbinder ohne Breite, weiche Bindestriche, Wortverbinder und ASCII-Steuerzeichen, die KI-Tools als Wasserzeichen verwenden könnten.",
        category: "technical"
      },
      {
        question: "Wird das Entfernen von Wasserzeichen meinen Text beeinflussen?",
        answer: "Nein. Das Tool entfernt nur unsichtbare Zeichen, die als Wasserzeichen verwendet werden. Ihr sichtbarer Text, die Formatierung und die Bedeutung bleiben genau gleich.",
        category: "basics"
      },
      {
        question: "Gibt es ein Zeichenlimit?",
        answer: "Ja, die kostenlose Version hat derzeit ein Limit von 500 Zeichen pro Operation. Angemeldete Benutzer erhalten 1.500 Zeichen. Dies hilft, eine schnelle Verarbeitung und optimale Leistung zu gewährleisten.",
        category: "limits"
      },
      {
        question: "Kann ich Wasserzeichen aus Word-Dokumenten entfernen?",
        answer: "Ja! Sie können Word-Dokumente (.docx) direkt in unser Tool hochladen. Wir scannen und entfernen unsichtbare Wasserzeichen aus allen Abschnitten, einschließlich Hauptinhalt, Kopfzeilen, Fußzeilen, Fußnoten und Kommentaren – alles unter Beibehaltung der Formatierung und des Layouts Ihres Dokuments. Die Verarbeitung erfolgt vollständig in Ihrem Browser, sodass Ihre Dokumente privat bleiben.",
        category: "technical"
      },
      {
        question: "Kann ich Wasserzeichen aus Apple Pages-Dokumenten entfernen?",
        answer: "Ja! Sie können Apple Pages-Dokumente (.pages) direkt in unser Tool hochladen. Wir unterstützen sowohl moderne als auch ältere Pages-Formate. Wir scannen und entfernen unsichtbare Wasserzeichen aus Ihrem gesamten Dokument, während alle Formatierungen, Stile und das Layout erhalten bleiben. Die gesamte Verarbeitung erfolgt lokal in Ihrem Browser, sodass Ihre Dokumente vollständig privat bleiben.",
        category: "technical"
      },
      {
        question: "Wie sicher ist die Dokumentenverarbeitung? Verlassen meine Dokumente mein Gerät?",
        answer: "Ihre Dokumente sind zu 100% sicher und verlassen niemals Ihr Gerät. Die gesamte Dokumentenverarbeitung erfolgt vollständig in Ihrem Browser mit JavaScript. Wenn Sie ein Word- oder Pages-Dokument hochladen, wird es lokal auf Ihrem Computer gelesen und geändert – wir senden Ihre Dateien niemals an einen Server. Das bedeutet, dass Ihre sensiblen Dokumente, persönlichen Informationen und privaten Inhalte vollständig vertraulich bleiben. Nicht einmal wir können sehen, was Sie verarbeiten. Dieser clientseitige Ansatz gewährleistet maximale Privatsphäre und Sicherheit für Ihre Dokumente.",
        category: "privacy"
      },
      {
        question: "Warum zeigt mein bereinigtes Dokument noch Wasserzeichen, wenn ich den Text kopiere?",
        answer: "Wenn Sie ein Dokument mit unserem Dokument-Uploader bereinigen und dann den Text in den Texterkennungsbereich kopieren, werden möglicherweise Wasserzeichen erkannt. Dies ist normal und bedeutet nicht, dass die Bereinigung fehlgeschlagen ist. Hier ist der Grund: Die Dokumentenbereinigung bewahrt alle Formatierungen (fett, kursiv, Abstände usw.), damit Ihr Dokument identisch aussieht. Wenn Sie diesen formatierten Text kopieren, kommen einige harmlose Formatierungszeichen (wie spezielle Leerzeichen oder Unicode-Formatierungsmarkierungen) mit. Unser Texterkennungstool, das aggressiver arbeitet, könnte diese Formatierungszeichen als potenzielle Wasserzeichen kennzeichnen. Das Dokument selbst ist sauber – dies sind nur Formatierungsartefakte aus dem Kopier-Einfügen-Prozess, keine tatsächlichen KI-Wasserzeichen. Für beste Ergebnisse verwenden Sie das bereinigte Dokument direkt, anstatt seinen Inhalt zu kopieren und einzufügen.",
        category: "technical"
      },
      {
        question: "Was ist ein Zero-Width-Zeichen und warum verwenden KI-Tools sie?",
        answer: "Zero-Width-Zeichen sind unsichtbare Unicode-Zeichen, die keinen visuellen Platz einnehmen, aber in den Textdaten existieren. Manche KI-Systeme, darunter ChatGPT, fügen diese als Nebenprodukt der Texterzeugung ein. Häufige Typen sind Zero-Width-Leerzeichen (U+200B), Zero-Width-Verbinder (U+200D) und Zero-Width-Nicht-Verbinder (U+200C). Unser Tool erkennt und entfernt diese unsichtbaren Markierungen.",
        category: "technical"
      },
      {
        question: "Wie schneidet dieses Tool im Vergleich zu anderen kostenlosen Wasserzeichen-Entfernern ab?",
        answer: "Unser Tool ist kostenlos, verarbeitet Text lokal in Ihrem Browser für mehr Privatsphäre und funktioniert mit Text aus jeder Quelle, da es auf Zeichenebene statt auf Modellebene arbeitet. Im Gegensatz zu anderen Tools speichern wir Ihren Text nie, bieten Dokumentenbereinigung für Word- und Pages-Dateien an und geben angemeldeten Nutzern ein Limit von 1.500 Zeichen, unbegrenzt mit Premium.",
        category: "basics"
      },
      {
        question: "Kann ich Text direkt aus ChatGPT einfügen und bereinigen?",
        answer: "Ja! Kopieren Sie einfach Text aus ChatGPT, Claude, Gemini oder einem anderen KI-Chat und fügen Sie ihn direkt in unser Tool ein. Unser Detektor für unsichtbare Zeichen hebt versteckte Zeichen hervor, und Sie können den Text mit einem Klick bereinigen. Dieser Einfüge-Workflow ist schnell und nahtlos.",
        category: "basics"
      },
      {
        question: "Wie erkenne ich KI-Wasserzeichen in meinem Text?",
        answer: "Fügen Sie Ihren Text in unser Erkennungstool ein. Wir scannen nach Dutzenden unsichtbarer Unicode-Zeichen, einschließlich Zero-Width-Leerzeichen, Zero-Width-Verbinder und anderer Zero-Width- und Steuerzeichen. Erkannte Wasserzeichen werden hervorgehoben, damit Sie genau sehen können, wo sie sich in Ihrem Text befinden.",
        category: "technical"
      }
    ],
    'pt-BR': [
      {
        question: "O que são marcas d'água de IA?",
        answer: "Marcas d'água de IA são caracteres invisíveis, como espaços de largura zero, que algumas ferramentas de IA inserem no texto gerado. Elas não afetam a legibilidade, e sua presença ou ausência não influencia se ferramentas de detecção de IA sinalizam o texto.",
        category: "basics"
      },
      {
        question: "Como funciona a ferramenta de remoção de marcas d'água?",
        answer: "Nossa ferramenta verifica seu texto em busca de caracteres invisíveis comumente usados como marcas d'água de IA, como espaços de largura zero e caracteres de controle ASCII. Em seguida, destaca esses marcadores e pode removê-los completamente, preservando seu conteúdo original.",
        category: "technical"
      },
      {
        question: "Meu texto é armazenado ou registrado?",
        answer: "Não. Todo o processamento de texto acontece diretamente no seu navegador. Não armazenamos, registramos ou transmitimos seu texto para lugar nenhum. Sua privacidade é nossa prioridade.",
        category: "privacy"
      },
      {
        question: "Que tipos de marcas d'água podem ser detectadas?",
        answer: "Detectamos vários caracteres invisíveis, incluindo espaços de largura zero, juntores de largura zero, hífens suaves, juntores de palavras e caracteres de controle ASCII que ferramentas de IA podem usar como marcas d'água.",
        category: "technical"
      },
      {
        question: "A remoção de marcas d'água afetará meu texto?",
        answer: "Não. A ferramenta remove apenas caracteres invisíveis usados como marcas d'água. Seu texto visível, formatação e significado permanecem exatamente os mesmos.",
        category: "basics"
      },
      {
        question: "Existe um limite de caracteres?",
        answer: "Sim, a versão gratuita atualmente tem um limite de 500 caracteres por operação. Usuários conectados recebem 1.500 caracteres. Isso ajuda a garantir processamento rápido e desempenho ideal.",
        category: "limits"
      },
      {
        question: "Posso remover marcas d'água de documentos Word?",
        answer: "Sim! Você pode fazer upload de documentos Word (.docx) diretamente em nossa ferramenta. Verificamos e removemos marcas d'água invisíveis de todas as seções, incluindo conteúdo principal, cabeçalhos, rodapés, notas de rodapé e comentários – tudo isso preservando a formatação e o layout do seu documento. O processamento é feito completamente no seu navegador, então seus documentos permanecem privados.",
        category: "technical"
      },
      {
        question: "Posso remover marcas d'água de documentos Apple Pages?",
        answer: "Sim! Você pode fazer upload de documentos Apple Pages (.pages) diretamente em nossa ferramenta. Suportamos formatos Pages modernos e antigos. Verificamos e removemos marcas d'água invisíveis de todo o seu documento, mantendo toda a formatação, estilos e layout. Todo o processamento é feito localmente no seu navegador, então seus documentos permanecem completamente privados.",
        category: "technical"
      },
      {
        question: "Quão seguro é o processamento de documentos? Meus documentos saem do meu dispositivo?",
        answer: "Seus documentos são 100% seguros e nunca saem do seu dispositivo. Todo o processamento de documentos é feito completamente no seu navegador usando JavaScript. Quando você faz upload de um documento Word ou Pages, ele é lido e modificado localmente no seu computador – nunca enviamos seus arquivos para um servidor. Isso significa que seus documentos sensíveis, informações pessoais e conteúdo privado permanecem completamente confidenciais. Nem mesmo nós podemos ver o que você está processando. Essa abordagem no lado do cliente garante máxima privacidade e segurança para seus documentos.",
        category: "privacy"
      },
      {
        question: "Por que meu documento limpo ainda mostra marcas d'água quando copio o texto?",
        answer: "Se você limpar um documento usando nosso carregador de documentos e depois copiar e colar o texto na área de detecção de texto, você pode ver marcas d'água detectadas. Isso é normal e não significa que a limpeza falhou. Veja o porquê: A limpeza de documentos preserva toda a formatação (negrito, itálico, espaçamento, etc.) para manter seu documento com aparência idêntica. Quando você copia esse texto formatado, alguns caracteres de formatação inofensivos (como espaços especiais ou marcas de formatação unicode) vêm junto. Nossa ferramenta de detecção de texto, que é projetada para ser mais agressiva, pode sinalizar esses caracteres de formatação como possíveis marcas d'água. O documento em si está limpo – esses são apenas artefatos de formatação do processo de copiar e colar, não marcas d'água de IA reais. Para melhores resultados, use o documento limpo diretamente, em vez de copiar e colar seu conteúdo.",
        category: "technical"
      },
      {
        question: "O que é um caractere de largura zero e por que as ferramentas de IA os usam?",
        answer: "Caracteres de largura zero são caracteres Unicode invisíveis que não ocupam espaço visual, mas existem nos dados do texto. Alguns sistemas de IA, incluindo o ChatGPT, inserem esses caracteres como um subproduto de como o conteúdo foi gerado. Tipos comuns incluem espaços de largura zero (U+200B), conectores de largura zero (U+200D) e não-conectores de largura zero (U+200C). Nossa ferramenta detecta e remove esses marcadores invisíveis.",
        category: "technical"
      },
      {
        question: "Como isso se compara a outros removedores gratuitos de marcas d'água?",
        answer: "Nossa ferramenta é gratuita, processa texto localmente no seu navegador para privacidade e funciona com texto de qualquer origem, porque opera em caracteres, não no modelo. Diferente de outras ferramentas, nunca armazenamos seu texto, oferecemos limpeza de documentos para arquivos Word e Pages, e damos aos usuários conectados um limite de 1.500 caracteres, ilimitado com o Premium.",
        category: "basics"
      },
      {
        question: "Posso colar texto diretamente do ChatGPT?",
        answer: "Sim! Simplesmente copie texto do ChatGPT, Claude, Gemini ou qualquer chat de IA e cole diretamente na nossa ferramenta. Nosso detector de caracteres invisíveis destaca quaisquer caracteres ocultos, e você pode limpar o texto com um clique. Este fluxo de colar é projetado para ser rápido e sem interrupções.",
        category: "basics"
      },
      {
        question: "Como detecto marcas d'água de IA no meu texto?",
        answer: "Cole seu texto na nossa ferramenta de detecção. Vamos escanear dezenas de tipos de caracteres Unicode invisíveis, incluindo espaços de largura zero, conectores de largura zero e outros caracteres de largura zero e de controle. As marcas d'água detectadas são destacadas para que você possa ver exatamente onde estão no seu texto.",
        category: "technical"
      }
    ],
    'ms-MY': [
      {
        question: "Apakah tanda air AI?",
        answer: "Tanda air AI adalah aksara tidak kelihatan, seperti ruang lebar sifar, yang dimasukkan oleh sesetengah alat AI ke dalam teks yang dijana. Ia tidak menjejaskan kebolehbacaan, dan kehadiran atau ketiadaannya tidak mempengaruhi sama ada alat pengesanan AI menandakan teks tersebut.",
        category: "basics"
      },
      {
        question: "Bagaimana alat penyingkiran tanda air berfungsi?",
        answer: "Alat kami mengimbas teks anda untuk aksara yang tidak kelihatan yang biasa digunakan sebagai tanda air AI, seperti ruang lebar sifar dan aksara kawalan ASCII. Kemudian ia menyerlahkan penanda ini dan boleh membuangnya sepenuhnya sambil mengekalkan kandungan asal anda.",
        category: "technical"
      },
      {
        question: "Adakah teks saya disimpan atau dilog?",
        answer: "Tidak. Semua pemprosesan teks berlaku terus dalam pelayar anda. Kami tidak menyimpan, merekod atau menghantar teks anda ke mana-mana. Privasi anda adalah keutamaan kami.",
        category: "privacy"
      },
      {
        question: "Apakah jenis tanda air yang boleh dikesan?",
        answer: "Kami mengesan pelbagai aksara yang tidak kelihatan, termasuk ruang lebar sifar, penyambung lebar sifar, tanda sempang lembut, penyambung perkataan dan aksara kawalan ASCII yang alat AI mungkin gunakan sebagai tanda air.",
        category: "technical"
      },
      {
        question: "Adakah penyingkiran tanda air akan menjejaskan teks saya?",
        answer: "Tidak. Alat ini hanya membuang aksara yang tidak kelihatan yang digunakan sebagai tanda air. Teks yang kelihatan, pemformatan dan makna anda kekal sama.",
        category: "basics"
      },
      {
        question: "Adakah terdapat had aksara?",
        answer: "Ya, versi percuma pada masa ini mempunyai had 500 aksara setiap operasi. Pengguna yang log masuk mendapat 1,500 aksara. Ini membantu memastikan pemprosesan pantas dan prestasi optimum.",
        category: "limits"
      },
      {
        question: "Bolehkah saya membuang tanda air dari dokumen Word?",
        answer: "Ya! Anda boleh memuat naik dokumen Word (.docx) terus ke alat kami. Kami mengimbas dan membuang tanda air yang tidak kelihatan dari semua bahagian, termasuk kandungan utama, pengepala, pengaki, nota kaki dan komen – semuanya sambil mengekalkan pemformatan dan susun atur dokumen anda. Pemprosesan dilakukan sepenuhnya dalam pelayar anda, jadi dokumen anda kekal peribadi.",
        category: "technical"
      },
      {
        question: "Bolehkah saya membuang tanda air dari dokumen Apple Pages?",
        answer: "Ya! Anda boleh memuat naik dokumen Apple Pages (.pages) terus ke alat kami. Kami menyokong kedua-dua format Pages moden dan lama. Kami mengimbas dan membuang tanda air yang tidak kelihatan dari keseluruhan dokumen anda sambil mengekalkan semua pemformatan, gaya dan susun atur. Semua pemprosesan dilakukan secara tempatan dalam pelayar anda, jadi dokumen anda kekal sepenuhnya peribadi.",
        category: "technical"
      },
      {
        question: "Sejauh mana selamat pemprosesan dokumen? Adakah dokumen saya meninggalkan peranti saya?",
        answer: "Dokumen anda 100% selamat dan tidak pernah meninggalkan peranti anda. Semua pemprosesan dokumen dilakukan sepenuhnya dalam pelayar anda menggunakan JavaScript. Apabila anda memuat naik dokumen Word atau Pages, ia dibaca dan diubah secara tempatan di komputer anda – kami tidak pernah menghantar fail anda ke pelayan. Ini bermakna dokumen sensitif, maklumat peribadi dan kandungan peribadi anda kekal sepenuhnya sulit. Walaupun kami tidak dapat melihat apa yang anda proses. Pendekatan sebelah klien ini memastikan privasi dan keselamatan maksimum untuk dokumen anda.",
        category: "privacy"
      },
      {
        question: "Mengapa dokumen yang telah dibersihkan masih menunjukkan tanda air apabila saya menyalin teks?",
        answer: "Jika anda membersihkan dokumen menggunakan pemuat naik dokumen kami dan kemudian menyalin teks ke kawasan pengesanan teks, anda mungkin melihat tanda air dikesan. Ini normal dan tidak bermakna pembersihan gagal. Inilah sebabnya: Pembersihan dokumen mengekalkan semua pemformatan (tebal, condong, jarak, dll.) untuk memastikan dokumen anda kelihatan sama. Apabila anda menyalin teks berformat ini, beberapa aksara pemformatan yang tidak berbahaya (seperti ruang khas atau tanda pemformatan unicode) turut disalin. Alat pengesanan teks kami, yang direka untuk lebih agresif, mungkin menandai aksara pemformatan ini sebagai tanda air yang berpotensi. Dokumen itu sendiri bersih – ini hanyalah artifak pemformatan dari proses salin-tampal, bukan tanda air AI sebenar. Untuk hasil terbaik, gunakan dokumen yang telah dibersihkan secara langsung daripada menyalin-tampal kandungannya.",
        category: "technical"
      },
      {
        question: "Apakah aksara lebar sifar dan mengapa alat AI menggunakannya?",
        answer: "Aksara lebar sifar adalah aksara Unicode yang tidak kelihatan yang tidak mengambil ruang visual tetapi wujud dalam data teks. Sesetengah sistem AI, termasuk ChatGPT, memasukkan aksara ini sebagai hasil sampingan cara kandungan itu dijana. Jenis biasa termasuk ruang lebar sifar (U+200B), penyambung lebar sifar (U+200D), dan bukan penyambung lebar sifar (U+200C). Alat kami mengesan dan membuang penanda tidak kelihatan ini.",
        category: "technical"
      },
      {
        question: "Bagaimana ini berbanding dengan penyingkir tanda air percuma yang lain?",
        answer: "Alat kami percuma, memproses teks secara tempatan dalam pelayar anda untuk privasi, dan berfungsi dengan teks dari mana-mana sumber, kerana ia beroperasi pada aksara dan bukan pada model. Berbeza dengan alat lain, kami tidak pernah menyimpan teks anda, menawarkan pembersihan dokumen untuk fail Word dan Pages, dan memberikan pengguna yang log masuk had 1,500 aksara, tanpa had dengan Premium.",
        category: "basics"
      },
      {
        question: "Bolehkah saya tampal teks terus dari ChatGPT?",
        answer: "Ya! Hanya salin teks dari ChatGPT, Claude, Gemini atau mana-mana chat AI dan tampal terus ke alat kami. Pengesan aksara tidak kelihatan kami akan menyerlahkan sebarang aksara tersembunyi, dan anda boleh membersihkan teks dengan satu klik. Aliran kerja tampal ini direka untuk cepat dan lancar.",
        category: "basics"
      },
      {
        question: "Bagaimana saya mengesan tanda air AI dalam teks saya?",
        answer: "Tampal teks anda ke dalam alat pengesan kami. Kami akan mengimbas puluhan jenis aksara Unicode tidak kelihatan, termasuk ruang lebar sifar, penyambung lebar sifar dan aksara lebar sifar serta kawalan lain. Tanda air yang dikesan diserlahkan supaya anda dapat melihat dengan tepat di mana ia berada dalam teks anda.",
        category: "technical"
      }
    ],
    es: [
      {
        question: "¿Qué son las marcas de agua de IA?",
        answer: "Las marcas de agua de IA son caracteres invisibles, como espacios de ancho cero, que algunas herramientas de IA insertan en el texto generado. No afectan la legibilidad, y su presencia o ausencia no influye en si las herramientas de detección de IA marcan el texto.",
        category: "basics"
      },
      {
        question: "¿Cómo funciona la herramienta de eliminación de marcas de agua?",
        answer: "Nuestra herramienta escanea tu texto en busca de caracteres invisibles comúnmente utilizados como marcas de agua de IA, como espacios de ancho cero y caracteres de control ASCII. Luego resalta estos marcadores y puede eliminarlos completamente mientras preserva tu contenido original.",
        category: "technical"
      },
      {
        question: "¿Se almacena o registra mi texto?",
        answer: "No. Todo el procesamiento de texto ocurre directamente en tu navegador. No almacenamos, registramos ni transmitimos tu texto a ningún lugar. Tu privacidad es nuestra prioridad.",
        category: "privacy"
      },
      {
        question: "¿Qué tipos de marcas de agua se pueden detectar?",
        answer: "Detectamos varios caracteres invisibles incluyendo espacios de ancho cero, conectores de ancho cero, guiones suaves, conectores de palabras y caracteres de control ASCII que las herramientas de IA podrían usar como marcas de agua.",
        category: "technical"
      },
      {
        question: "¿Eliminar las marcas de agua afectará mi texto?",
        answer: "No. La herramienta solo elimina los caracteres invisibles utilizados como marcas de agua. Tu texto visible, formato y significado permanecen exactamente iguales.",
        category: "basics"
      },
      {
        question: "¿Hay un límite de caracteres?",
        answer: "Sí, la versión gratuita actualmente tiene un límite de 500 caracteres por operación. Los usuarios que inician sesión obtienen 1,500 caracteres. Esto ayuda a garantizar un procesamiento rápido y un rendimiento óptimo.",
        category: "limits"
      },
      {
        question: "¿Puedo limpiar marcas de agua de documentos Word?",
        answer: "¡Sí! Puedes cargar documentos Word (.docx) directamente en nuestra herramienta. Escaneamos y eliminamos caracteres de marca de agua invisibles de todas las secciones, incluido el contenido principal, encabezados, pies de página, notas al pie y comentarios, todo mientras preservamos el formato y diseño de tu documento. El procesamiento ocurre completamente en tu navegador, por lo que tus documentos permanecen privados.",
        category: "technical"
      },
      {
        question: "¿Puedo limpiar marcas de agua de documentos Apple Pages?",
        answer: "¡Sí! Puedes cargar documentos Apple Pages (.pages) directamente en nuestra herramienta. Soportamos tanto formatos modernos como antiguos de Pages. Escaneamos y eliminamos caracteres de marca de agua invisibles de todo tu documento mientras preservamos todo el formato, estilos y diseño. Todo el procesamiento ocurre localmente en tu navegador, asegurando que tus documentos permanezcan completamente privados.",
        category: "technical"
      },
      {
        question: "¿Qué tan seguro es el procesamiento de documentos? ¿Mis documentos salen de mi dispositivo?",
        answer: "Tus documentos están 100% seguros y nunca salen de tu dispositivo. Todo el procesamiento de documentos ocurre completamente en tu navegador usando JavaScript. Cuando cargas un documento Word o Pages, se lee y modifica localmente en tu computadora: nunca enviamos tus archivos a ningún servidor. Esto significa que tus documentos sensibles, información personal y contenido privado permanecen completamente confidenciales. Ni siquiera nosotros podemos ver lo que estás procesando. Este enfoque del lado del cliente garantiza la máxima privacidad y seguridad para tus documentos.",
        category: "privacy"
      },
      {
        question: "¿Por qué mi documento limpio todavía muestra marcas de agua cuando copio el texto?",
        answer: "Si limpias un documento usando nuestro cargador de documentos y luego copias y pegas el texto en el área de detección de texto, podrías ver marcas de agua detectadas. Esto es normal y no significa que la limpieza haya fallado. Aquí está el porqué: La limpieza de documentos preserva todo el formato (negrita, cursiva, espaciado, etc.) para mantener tu documento idéntico. Cuando copias este texto formateado, algunos caracteres de formato inofensivos (como espacios especiales o marcas de formato unicode) vienen incluidos. Nuestra herramienta de detección de texto, que está diseñada para ser más agresiva, podría marcar estos caracteres de formato como posibles marcas de agua. El documento en sí está limpio: estos son solo artefactos de formato del proceso de copiar y pegar, no marcas de agua de IA reales. Para mejores resultados, usa el documento limpio directamente en lugar de copiar y pegar su contenido.",
        category: "technical"
      },
      {
        question: "¿Qué es un carácter de ancho cero y por qué las herramientas de IA los usan?",
        answer: "Los caracteres de ancho cero son caracteres Unicode invisibles que no ocupan espacio visual pero existen en los datos del texto. Algunos sistemas de IA, incluido ChatGPT, los insertan como un subproducto de cómo se generó el contenido. Los tipos comunes incluyen espacios de ancho cero (U+200B), conectores de ancho cero (U+200D) y no-conectores de ancho cero (U+200C). Nuestra herramienta detecta y elimina estos marcadores invisibles.",
        category: "technical"
      },
      {
        question: "¿Cómo se compara esto con otros eliminadores de marcas de agua gratuitos?",
        answer: "Nuestra herramienta es gratuita, procesa texto localmente en tu navegador para mayor privacidad y funciona con texto de cualquier origen, porque opera sobre caracteres y no sobre el modelo. A diferencia de otras herramientas, nunca almacenamos tu texto, ofrecemos limpieza de documentos para archivos Word y Pages, y damos a los usuarios registrados un límite de 1.500 caracteres, ilimitado con Premium.",
        category: "basics"
      },
      {
        question: "¿Puedo pegar texto directamente desde ChatGPT?",
        answer: "¡Sí! Simplemente copia texto de ChatGPT, Claude, Gemini o cualquier chat de IA y pégalo directamente en nuestra herramienta. Nuestro detector de caracteres invisibles resalta cualquier carácter oculto, y puedes limpiar el texto con un clic. Este flujo de pegado está diseñado para ser rápido y sin interrupciones.",
        category: "basics"
      },
      {
        question: "¿Cómo detecto marcas de agua de IA en mi texto?",
        answer: "Pega tu texto en nuestra herramienta de detección. Escanearemos decenas de tipos de caracteres Unicode invisibles, incluyendo espacios de ancho cero, conectores de ancho cero y otros caracteres de ancho cero y de control. Las marcas de agua detectadas se resaltan para que puedas ver exactamente dónde están en tu texto.",
        category: "technical"
      }
    ],
    fr: [
      {
        question: "Qu'est-ce qu'un filigrane IA ?",
        answer: "Les filigranes IA sont des caractères invisibles, comme les espaces de largeur nulle, que certains outils d'IA insèrent dans le texte généré. Ils n'altèrent pas la lisibilité, et leur présence ou absence n'a aucune incidence sur le fait qu'un détecteur d'IA signale le texte.",
        category: "basics"
      },
      {
        question: "Comment fonctionne votre outil de suppression ?",
        answer: "Notre outil analyse votre texte pour détecter des dizaines de caractères Unicode invisibles, notamment les espaces de largeur nulle et les caractères de contrôle. Il met en évidence ces marqueurs et peut les supprimer entièrement tout en préservant votre contenu original et sa mise en forme.",
        category: "technical"
      },
      {
        question: "Mon texte est-il stocké ou enregistré ?",
        answer: "Non, absolument pas. Tout le traitement s'effectue directement dans votre navigateur. Nous ne stockons, n'enregistrons ni ne transmettons jamais votre texte. Votre confidentialité est notre priorité absolue, conformément aux standards RGPD.",
        category: "privacy"
      },
      {
        question: "Quels types de filigranes peuvent être détectés ?",
        answer: "Nous détectons les espaces de largeur nulle, les jointures de largeur nulle, les tirets conditionnels, les jointures de mots et les caractères de contrôle ASCII. Ces caractères invisibles apparaissent dans du texte produit par des outils d'IA.",
        category: "technical"
      },
      {
        question: "La suppression des filigranes modifie-t-elle mon texte ?",
        answer: "Non. L'outil supprime uniquement les caractères invisibles utilisés comme filigranes. Votre texte visible, la mise en forme et le sens restent strictement identiques. Aucune modification du contenu n'est effectuée.",
        category: "basics"
      },
      {
        question: "Y a-t-il une limite de caractères ?",
        answer: "La version gratuite permet de traiter 500 caractères par opération. Les utilisateurs connectés bénéficient de 1 500 caractères. Cette limite assure un traitement rapide et des performances optimales pour tous les utilisateurs.",
        category: "limits"
      },
      {
        question: "Puis-je nettoyer des documents Word ?",
        answer: "Oui ! Vous pouvez importer directement vos documents Word (.docx). Nous analysons et supprimons les filigranes invisibles de toutes les sections : contenu principal, en-têtes, pieds de page, notes de bas de page et commentaires. La mise en page de votre document est intégralement préservée.",
        category: "technical"
      },
      {
        question: "Les documents Apple Pages sont-ils pris en charge ?",
        answer: "Oui ! Notre outil prend en charge les documents Apple Pages (.pages), formats modernes et anciens. Nous analysons et supprimons les filigranes invisibles tout en conservant la mise en forme, les styles et la mise en page. Le traitement s'effectue localement dans votre navigateur.",
        category: "technical"
      },
      {
        question: "Mes documents quittent-ils mon appareil ?",
        answer: "Jamais. Le traitement s'effectue entièrement dans votre navigateur via JavaScript. Quand vous importez un document, il est lu et modifié localement sur votre ordinateur. Vos fichiers ne sont jamais envoyés vers un serveur. Même nous ne pouvons pas voir ce que vous traitez.",
        category: "privacy"
      },
      {
        question: "Pourquoi des filigranes apparaissent-ils encore après le nettoyage ?",
        answer: "Si vous nettoyez un document puis copiez-collez le texte dans l'outil de détection, vous pourriez voir des filigranes détectés. C'est normal : le nettoyage préserve la mise en forme (gras, italique, espacement). Ces caractères de formatage peuvent être signalés par notre détecteur, mais ce ne sont pas de véritables filigranes IA.",
        category: "technical"
      },
      {
        question: "Qu'est-ce qu'un caractère de largeur nulle ?",
        answer: "Les caractères de largeur nulle sont des caractères Unicode invisibles qui n'occupent aucun espace visuel mais existent dans les données du texte. Certains systèmes d'IA, dont ChatGPT, les insèrent comme sous-produit de la génération du contenu. Types courants : espace de largeur nulle (U+200B), jointure de largeur nulle (U+200D), non-jointure de largeur nulle (U+200C).",
        category: "technical"
      },
      {
        question: "En quoi cela se compare-t-il aux autres outils gratuits de suppression de filigranes ?",
        answer: "Notre outil est gratuit, traite le texte localement dans votre navigateur pour la confidentialité, et fonctionne avec du texte provenant de n'importe quelle source, car il agit sur les caractères et non sur le modèle. Contrairement à d'autres outils, nous ne stockons jamais votre texte, proposons le nettoyage de documents Word et Pages, et offrons aux utilisateurs connectés une limite de 1 500 caractères, illimitée avec Premium.",
        category: "basics"
      },
      {
        question: "Puis-je coller directement depuis ChatGPT ?",
        answer: "Oui ! Copiez simplement le texte depuis ChatGPT, Claude, Gemini ou tout autre assistant IA et collez-le dans notre outil. Notre détecteur met en évidence les caractères cachés, et vous pourrez nettoyer le texte en un clic.",
        category: "basics"
      },
      {
        question: "Comment détecter les filigranes IA dans mon texte ?",
        answer: "Collez votre texte dans notre outil de détection. Nous analyserons des dizaines de types de caractères Unicode invisibles, notamment les espaces de largeur nulle, les jointures de largeur nulle et d'autres caractères de largeur nulle et de contrôle. Les filigranes détectés sont mis en évidence pour que vous puissiez voir précisément où ils se trouvent.",
        category: "technical"
      }
    ],
    ja: [
      {
        question: "AI透かしとは何ですか？",
        answer: "AI透かしとは、ゼロ幅スペースなど、一部のAIツールが生成テキストに挿入する不可視文字です。読みやすさには影響せず、その有無はAI検出ツールがテキストをAI生成と判定するかどうかにも関係しません。",
        category: "basics"
      },
      {
        question: "透かし除去ツールはどのように機能しますか？",
        answer: "当ツールは、数十種類の不可視Unicode文字をスキャンします。ゼロ幅スペース、ゼロ幅接合子、ASCIIコントロール文字などを検出・ハイライト表示し、元のコンテンツを保持したまま完全に削除できます。",
        category: "technical"
      },
      {
        question: "テキストは保存されますか？",
        answer: "いいえ。すべての処理はお使いのブラウザ内で直接行われます。テキストの保存、記録、送信は一切行いません。プライバシー保護を最優先としています。",
        category: "privacy"
      },
      {
        question: "どのような種類の透かしを検出できますか？",
        answer: "ゼロ幅スペース、ゼロ幅接合子、ソフトハイフン、ワードジョイナー、ASCIIコントロール文字など、AIツールが透かしとして使用する様々な不可視文字を検出します。",
        category: "technical"
      },
      {
        question: "透かしを削除するとテキストに影響がありますか？",
        answer: "いいえ。ツールは透かしとして使用される不可視文字のみを削除します。表示されるテキスト、書式設定、意味はそのまま維持されます。",
        category: "basics"
      },
      {
        question: "文字数制限はありますか？",
        answer: "はい、無料版は1回の操作につき500文字までです。ログインユーザーは1,500文字まで処理できます。これにより、高速処理と最適なパフォーマンスを確保しています。",
        category: "limits"
      },
      {
        question: "Wordドキュメントの透かしを削除できますか？",
        answer: "はい！Word文書（.docx）を直接アップロードできます。本文、ヘッダー、フッター、脚注、コメントを含むすべてのセクションから不可視の透かし文字をスキャン・削除します。ドキュメントの書式とレイアウトは完全に保持されます。",
        category: "technical"
      },
      {
        question: "Apple Pagesドキュメントに対応していますか？",
        answer: "はい！Apple Pages文書（.pages）を直接アップロードできます。最新版と旧版の両方のPagesフォーマットに対応しています。すべての書式、スタイル、レイアウトを維持しながら、ドキュメント全体から不可視の透かし文字を削除します。",
        category: "technical"
      },
      {
        question: "ドキュメント処理のセキュリティは安全ですか？",
        answer: "ドキュメントは100%安全で、デバイスから外部に送信されることは一切ありません。すべての処理はブラウザ内でJavaScriptを使用して行われます。WordやPagesドキュメントをアップロードすると、お使いのコンピュータ上でローカルに読み取り・変更されます。サーバーへの送信は行いません。",
        category: "privacy"
      },
      {
        question: "クリーニング後もテキストをコピーすると透かしが表示されるのはなぜですか？",
        answer: "ドキュメントをクリーニングした後、テキストをコピーして検出エリアに貼り付けると、透かしが検出される場合があります。これは正常な動作です。ドキュメントクリーニングは書式（太字、斜体、スペースなど）を保持するため、書式設定文字がコピーされることがあります。検出ツールがこれらを透かしとしてフラグを立てることがありますが、実際のAI透かしではありません。",
        category: "technical"
      },
      {
        question: "ゼロ幅文字とは何ですか？なぜAIツールは使用するのですか？",
        answer: "ゼロ幅文字は、視覚的なスペースを占有しないがテキストデータに存在する不可視のUnicode文字です。ChatGPTを含む一部のAIシステムは、コンテンツ生成の副産物としてこれらを挿入します。一般的な種類：ゼロ幅スペース（U+200B）、ゼロ幅接合子（U+200D）、ゼロ幅非接合子（U+200C）。",
        category: "technical"
      },
      {
        question: "他の無料透かし除去ツールと比べてどうですか？",
        answer: "当ツールは無料で、プライバシー保護のためテキストをブラウザ内でローカル処理し、モデルではなく文字単位で動作するため、どのようなソースのテキストにも対応します。他のツールとは異なり、テキストを保存せず、WordやPagesファイルのドキュメントクリーニングも提供し、ログインユーザーには1,500文字までの制限（Premiumなら無制限）を設けています。",
        category: "basics"
      },
      {
        question: "ChatGPTから直接テキストを貼り付けられますか？",
        answer: "はい！ChatGPT、Claude、Geminiなどからテキストをコピーして、当ツールに直接貼り付けてください。不可視文字検出機能が隠れた文字をハイライト表示し、ワンクリックでテキストをクリーニングできます。",
        category: "basics"
      },
      {
        question: "テキスト内のAI透かしを検出するにはどうすればよいですか？",
        answer: "テキストを検出ツールに貼り付けてください。ゼロ幅スペース、ゼロ幅接合子、その他のゼロ幅文字や制御文字を含む、数十種類の不可視Unicode文字をスキャンします。検出された透かしはハイライト表示され、テキスト内の正確な位置を確認できます。",
        category: "technical"
      }
    ],
    id: [
      {
        question: "Apa itu watermark AI?",
        answer: "Watermark AI adalah karakter tak terlihat, seperti spasi lebar nol, yang disisipkan oleh sebagian alat AI ke dalam teks yang dihasilkan. Karakter ini tidak mempengaruhi keterbacaan, dan keberadaannya tidak memengaruhi apakah alat deteksi AI menandai teks tersebut sebagai buatan AI.",
        category: "basics"
      },
      {
        question: "Bagaimana cara kerja alat penghapus watermark?",
        answer: "Alat kami memindai teks Anda untuk mencari puluhan jenis karakter Unicode tak terlihat yang umum digunakan sebagai watermark AI. Termasuk spasi lebar nol dan karakter kontrol ASCII. Alat ini menyorot penanda tersebut dan dapat menghapusnya sepenuhnya sambil menjaga konten asli Anda.",
        category: "technical"
      },
      {
        question: "Apakah teks saya disimpan atau dicatat?",
        answer: "Tidak. Semua pemrosesan teks dilakukan langsung di browser Anda. Kami tidak menyimpan, mencatat, atau mengirimkan teks Anda ke mana pun. Privasi Anda adalah prioritas utama kami.",
        category: "privacy"
      },
      {
        question: "Jenis watermark apa saja yang bisa dideteksi?",
        answer: "Kami mendeteksi berbagai karakter tak terlihat termasuk spasi lebar nol, zero-width joiner, soft hyphen, word joiner, dan karakter kontrol ASCII yang mungkin digunakan tools AI sebagai watermark.",
        category: "technical"
      },
      {
        question: "Apakah menghapus watermark akan mempengaruhi teks saya?",
        answer: "Tidak. Alat ini hanya menghapus karakter tak terlihat yang digunakan sebagai watermark. Teks yang terlihat, format, dan makna tetap sama persis seperti aslinya.",
        category: "basics"
      },
      {
        question: "Apakah ada batasan karakter?",
        answer: "Ya, versi gratis saat ini memiliki batas 500 karakter per operasi. Pengguna yang login mendapatkan 1.500 karakter. Ini membantu memastikan pemrosesan cepat dan performa optimal.",
        category: "limits"
      },
      {
        question: "Bisakah saya membersihkan watermark dari dokumen Word?",
        answer: "Bisa! Anda dapat mengunggah dokumen Word (.docx) langsung ke alat kami. Kami memindai dan menghapus karakter watermark tak terlihat dari semua bagian termasuk konten utama, header, footer, catatan kaki, dan komentar — semuanya dengan tetap menjaga format dan tata letak dokumen Anda.",
        category: "technical"
      },
      {
        question: "Apakah dokumen Apple Pages didukung?",
        answer: "Ya! Anda dapat mengunggah dokumen Apple Pages (.pages) langsung ke alat kami. Kami mendukung format Pages modern dan lama. Kami memindai dan menghapus karakter watermark tak terlihat dari seluruh dokumen sambil menjaga semua format, gaya, dan tata letak. Semua pemrosesan dilakukan secara lokal di browser Anda.",
        category: "technical"
      },
      {
        question: "Seberapa aman pemrosesan dokumen? Apakah dokumen saya keluar dari perangkat?",
        answer: "Dokumen Anda 100% aman dan tidak pernah meninggalkan perangkat Anda. Semua pemrosesan dokumen dilakukan sepenuhnya di browser Anda menggunakan JavaScript. Saat Anda mengunggah dokumen Word atau Pages, dokumen dibaca dan dimodifikasi secara lokal di komputer Anda — kami tidak pernah mengirim file Anda ke server mana pun.",
        category: "privacy"
      },
      {
        question: "Mengapa dokumen yang sudah dibersihkan masih menunjukkan watermark saat saya menyalin teks?",
        answer: "Jika Anda membersihkan dokumen menggunakan pengunggah dokumen kami lalu menyalin teks ke area deteksi teks, Anda mungkin melihat watermark terdeteksi. Ini normal dan bukan berarti pembersihan gagal. Pembersihan dokumen menjaga semua format (tebal, miring, spasi, dll.). Saat Anda menyalin teks berformat ini, beberapa karakter format yang tidak berbahaya ikut tersalin. Alat deteksi kami mungkin menandai karakter format ini sebagai potensi watermark. Dokumen itu sendiri sudah bersih.",
        category: "technical"
      },
      {
        question: "Apa itu karakter lebar nol dan mengapa tools AI menggunakannya?",
        answer: "Karakter lebar nol adalah karakter Unicode tak terlihat yang tidak memakan ruang visual tapi ada dalam data teks. Beberapa sistem AI, termasuk ChatGPT, menyisipkan karakter ini sebagai hasil sampingan dari cara konten dihasilkan. Jenis umum meliputi: spasi lebar nol (U+200B), zero-width joiner (U+200D), dan zero-width non-joiner (U+200C).",
        category: "technical"
      },
      {
        question: "Bagaimana perbandingannya dengan penghapus watermark gratis lainnya?",
        answer: "Alat kami gratis, memproses teks secara lokal di browser Anda demi privasi, dan bekerja pada teks dari sumber mana pun karena beroperasi pada karakter, bukan pada model. Berbeda dengan alat lain, kami tidak pernah menyimpan teks Anda, menawarkan pembersihan dokumen untuk file Word dan Pages, dan memberi pengguna yang login batas 1.500 karakter, tanpa batas dengan Premium.",
        category: "basics"
      },
      {
        question: "Bisakah saya langsung menempelkan teks dari ChatGPT?",
        answer: "Bisa! Cukup salin teks dari ChatGPT, Claude, Gemini, atau chat AI lainnya dan tempelkan langsung ke alat kami. Detektor karakter tak terlihat kami akan menyorot karakter tersembunyi, dan Anda dapat membersihkan teks dengan satu klik.",
        category: "basics"
      },
      {
        question: "Bagaimana cara mendeteksi watermark AI di teks saya?",
        answer: "Tempelkan teks Anda ke alat detektor kami. Kami akan memindai puluhan jenis karakter Unicode tak terlihat, termasuk spasi lebar nol, zero-width joiner, dan karakter lebar nol serta kontrol lainnya. Watermark yang terdeteksi akan disorot sehingga Anda dapat melihat persis di mana letaknya dalam teks Anda.",
        category: "technical"
      }
    ]
  };

  const toggleFAQ = (index: number) => {
    const isOpening = openIndex !== index;
    setOpenIndex(openIndex === index ? null : index);

    // Analytics removed
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'basics':
        return Sparkles;
      case 'technical':
        return Cog;
      case 'privacy':
        return Lock;
      case 'limits':
        return Gauge;
      default:
        return HelpCircle;
    }
  };

  const getIconForQuestion = (question: string) => {
    // Map specific questions to specific icons
    const questionLower = question.toLowerCase();

    if (questionLower.includes('was sind') || questionLower.includes('what are')) {
      return Sparkles;
    }
    if (questionLower.includes('wie funktioniert') || questionLower.includes('how does')) {
      return Cog;
    }
    if (questionLower.includes('gespeichert') || questionLower.includes('stored') ||
        questionLower.includes('secure') || questionLower.includes('sicher') ||
        questionLower.includes('verlassen') || questionLower.includes('leave')) {
      return Lock;
    }
    if (questionLower.includes('welche arten') || questionLower.includes('what types')) {
      return Search;
    }
    if (questionLower.includes('beeinflussen') || questionLower.includes('affect')) {
      return FileText;
    }
    if (questionLower.includes('limit')) {
      return Gauge;
    }
    if (questionLower.includes('kopiere') || questionLower.includes('copy') ||
        questionLower.includes('noch wasserzeichen') || questionLower.includes('still show')) {
      return FileText;
    }
    return HelpCircle;
  };

  return (
    <div className="mt-16">
      <div className="bg-gradient-to-br from-accent-50 via-neutral-100 to-purple-50 dark:bg-charcoal-900 rounded-2xl shadow-xl dark:shadow-dark-lg border border-neutral-200 dark:border-charcoal-700/50 overflow-hidden relative subtle-texture">
        {/* Decorative gradient orbs - visible only in dark mode */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-accent-400/10 to-purple-400/10 rounded-full blur-3xl opacity-0 dark:opacity-100"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-400/10 to-accent-400/10 rounded-full blur-3xl opacity-0 dark:opacity-100"></div>

        <div className="relative p-8 md:p-12">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-accent-200/50 dark:bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg mb-4 border border-accent-300 dark:border-white/15 shadow-sm">
              <MessageCircle className="w-5 h-5 text-accent-700 dark:text-white" />
              <span className="font-semibold text-sm text-accent-800 dark:text-white">
                {locale === 'de'
                  ? 'Hilfe & Support'
                  : locale === 'pt-BR'
                  ? 'Ajuda e Suporte'
                  : locale === 'ms-MY'
                  ? 'Bantuan & Sokongan'
                  : locale === 'es'
                  ? 'Ayuda y Soporte'
                  : locale === 'fr'
                  ? 'Aide & Support'
                  : locale === 'ja'
                  ? 'ヘルプ & サポート'
                  : locale === 'id'
                  ? 'Bantuan & Dukungan'
                  : 'Help & Support'}
              </span>
            </div>

            <HeadingTag className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight text-neutral-900 dark:text-white">
              {getTranslation(locale, 'faq.title')}
            </HeadingTag>

            <p className="text-base md:text-lg text-neutral-700 dark:text-neutral-200 mb-8 max-w-3xl mx-auto leading-relaxed">
              {locale === 'de'
                ? 'Finden Sie Antworten auf die häufigsten Fragen zu unserem KI-Wasserzeichen-Entferner.'
                : locale === 'pt-BR'
                ? 'Encontre respostas para as perguntas mais comuns sobre nosso removedor de marcas d\'água de IA.'
                : locale === 'ms-MY'
                ? 'Cari jawapan kepada soalan yang paling biasa tentang alat penyingkiran tanda air AI kami.'
                : locale === 'es'
                ? 'Encuentra respuestas a las preguntas más comunes sobre nuestro eliminador de marcas de agua de IA.'
                : locale === 'fr'
                ? 'Trouvez les réponses aux questions les plus fréquentes sur notre outil de suppression de filigranes IA.'
                : locale === 'ja'
                ? 'AI透かし除去ツールに関するよくある質問への回答をご覧ください。'
                : locale === 'id'
                ? 'Temukan jawaban untuk pertanyaan yang paling sering diajukan tentang alat penghapus watermark AI kami.'
                : 'Find answers to the most common questions about our AI watermark remover.'
              }
            </p>
          </div>
          
          {/* FAQ Items */}
          <div className="space-y-3">
            {faqs[locale].map((faq, index) => {
              const IconComponent = getIconForQuestion(faq.question);
              return (
                <div
                  key={index}
                  className="group border border-neutral-200 dark:border-charcoal-700/50 rounded-xl overflow-hidden bg-white/60 dark:bg-white/5 backdrop-blur-sm transition-colors duration-300 hover:bg-white/80 dark:hover:bg-white/10"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between professional-transition"
                  >
                    <div className="flex items-center gap-3 flex-grow">
                      <div className={`p-2 bg-accent-200 dark:bg-white/15 rounded-lg flex-shrink-0`}>
                        <IconComponent className="w-5 h-5 text-accent-700 dark:text-white" />
                      </div>
                      <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                        {faq.question}
                      </h3>
                    </div>
                    <div className={`p-1.5 rounded-lg transition-[background-color,transform] duration-300 ${openIndex === index ? 'bg-accent-200/50 dark:bg-white/10 rotate-180' : 'bg-neutral-100 dark:bg-white/5'}`}>
                      <ChevronDown className={`w-4 h-4 transition-colors ${openIndex === index ? 'text-accent-700 dark:text-white' : 'text-neutral-500 dark:text-neutral-400'}`} />
                    </div>
                  </button>
                  <div
                    className={`px-6 overflow-hidden transition-[max-height,padding] duration-300 ease-out ${
                      openIndex === index ? 'max-h-96 pb-5' : 'max-h-0'
                    }`}
                    style={{ contentVisibility: 'auto', containIntrinsicSize: '0 200px' }}
                  >
                    <div className="pl-11 border-l-2 border-accent-200 dark:border-white/10 ml-2">
                      <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed pl-4">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="mt-8 text-center">
            <div className="p-6 bg-white/60 dark:bg-white/5 backdrop-blur-sm rounded-xl border border-accent-200 dark:border-white/10">
              <h4 className="text-lg font-bold text-neutral-900 dark:text-white mb-2">
                {locale === 'de' ? 'Weitere Fragen?'
                  : locale === 'pt-BR' ? 'Mais perguntas?'
                  : locale === 'ms-MY' ? 'Ada soalan lain?'
                  : locale === 'es' ? '¿Más preguntas?'
                  : locale === 'fr' ? 'D\'autres questions ?'
                  : locale === 'ja' ? 'ご質問がありますか？'
                  : locale === 'id' ? 'Ada pertanyaan lain?'
                  : 'More Questions?'}
              </h4>
              <p className="text-sm text-neutral-700 dark:text-neutral-300">
                {locale === 'de'
                  ? 'Probieren Sie unser Tool aus und entdecken Sie selbst, wie einfach es ist!'
                  : locale === 'pt-BR'
                  ? 'Experimente nossa ferramenta e descubra como é fácil!'
                  : locale === 'ms-MY'
                  ? 'Cuba alat kami dan temui sendiri betapa mudahnya!'
                  : locale === 'es'
                  ? '¡Prueba nuestra herramienta y descubre lo fácil que es!'
                  : locale === 'fr'
                  ? 'Essayez notre outil et découvrez par vous-même sa simplicité !'
                  : locale === 'ja'
                  ? '当ツールをお試しください。使いやすさを実感していただけます。'
                  : locale === 'id'
                  ? 'Coba alat kami dan temukan sendiri betapa mudahnya!'
                  : 'Try our tool and discover for yourself how easy it is!'
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;